import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Building2, Plus, Trash2, Eye, ExternalLink, Copy, Upload, Image, X } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);
const STATUS_COLORS = { active: 'bg-emerald-50 text-emerald-700', under_contract: 'bg-amber-50 text-amber-700', sold: 'bg-slate-100 text-slate-500', off_market: 'bg-red-50 text-red-600' };

export default function ListingsTab() {
  const [listings, setListings] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: '', property_type: 'residential', address: '', city: '', state: 'NY', price: '', description: '', bedrooms: '', bathrooms: '', sqft: '', status: 'active' });
  const [uploading, setUploading] = useState(null);
  const fileRef = useRef(null);

  const fetch = useCallback(async () => { try { const r = await axios.get(`${API}/listings`, { withCredentials: true }); setListings(r.data); } catch {} }, []);
  useEffect(() => { fetch(); }, [fetch]);

  const save = async () => {
    if (!form.title || !form.address || !form.price) { toast.error('Title, address, and price required'); return; }
    const payload = { ...form, price: parseFloat(form.price), bedrooms: form.bedrooms ? parseInt(form.bedrooms) : null, bathrooms: form.bathrooms ? parseFloat(form.bathrooms) : null, sqft: form.sqft ? parseInt(form.sqft) : null };
    try { await axios.post(`${API}/listings`, payload, { withCredentials: true }); toast.success('Listing created'); setShowForm(false); fetch(); } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };
  const remove = async (id) => { try { await axios.delete(`${API}/listings/${id}`, { withCredentials: true }); toast.success('Deleted'); fetch(); } catch { toast.error('Failed'); } };
  const copyLink = (slug) => { navigator.clipboard.writeText(`${window.location.origin}/listing/${slug}`); toast.success('Link copied!'); };

  const uploadPhoto = async (listingId, file) => {
    setUploading(listingId);
    const fd = new FormData();
    fd.append('file', file);
    try {
      await axios.post(`${API}/listings/${listingId}/photos`, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Photo uploaded');
      fetch();
    } catch { toast.error('Upload failed'); }
    setUploading(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  const deletePhoto = async (listingId, index) => {
    try { await axios.delete(`${API}/listings/${listingId}/photos/${index}`, { withCredentials: true }); toast.success('Photo removed'); fetch(); } catch { toast.error('Failed'); }
  };

  return (
    <div data-testid="listings-tab">
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="text-2xl font-['Cabinet_Grotesk'] font-bold text-slate-900">My Listings</h1><p className="text-sm text-slate-400">Create shareable listing showcase pages.</p></div>
        {!showForm && <Button onClick={() => setShowForm(true)} className="bg-[#002FA7] text-white text-xs gap-1.5 rounded-lg" data-testid="add-listing-btn"><Plus size={14} /> New Listing</Button>}
      </div>

      {showForm && (
        <div className="bg-white border border-slate-200 rounded-xl p-5 mb-4" data-testid="listing-form">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
            <div className="sm:col-span-2"><Label className="text-xs text-slate-500">Title *</Label><Input value={form.title} onChange={e => setForm(f => ({...f, title: e.target.value}))} placeholder="Luxury 2BR Condo - Manhattan" data-testid="listing-title" /></div>
            <div><Label className="text-xs text-slate-500">Type</Label><Select value={form.property_type} onValueChange={v => setForm(f => ({...f, property_type: v}))}><SelectTrigger className="text-xs"><SelectValue /></SelectTrigger><SelectContent>{['residential', 'commercial', 'multifamily', 'office', 'retail', 'industrial', 'land'].map(t => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}</SelectContent></Select></div>
            <div><Label className="text-xs text-slate-500">Address *</Label><Input value={form.address} onChange={e => setForm(f => ({...f, address: e.target.value}))} data-testid="listing-address" /></div>
            <div><Label className="text-xs text-slate-500">City</Label><Input value={form.city} onChange={e => setForm(f => ({...f, city: e.target.value}))} /></div>
            <div><Label className="text-xs text-slate-500">State</Label><Select value={form.state} onValueChange={v => setForm(f => ({...f, state: v}))}><SelectTrigger className="text-xs"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="NY">New York</SelectItem><SelectItem value="PA">Pennsylvania</SelectItem><SelectItem value="FL">Florida</SelectItem></SelectContent></Select></div>
            <div><Label className="text-xs text-slate-500">Price *</Label><Input type="number" value={form.price} onChange={e => setForm(f => ({...f, price: e.target.value}))} data-testid="listing-price" /></div>
            <div><Label className="text-xs text-slate-500">Beds</Label><Input type="number" value={form.bedrooms} onChange={e => setForm(f => ({...f, bedrooms: e.target.value}))} /></div>
            <div><Label className="text-xs text-slate-500">Baths</Label><Input type="number" step="0.5" value={form.bathrooms} onChange={e => setForm(f => ({...f, bathrooms: e.target.value}))} /></div>
            <div className="sm:col-span-3"><Label className="text-xs text-slate-500">Description</Label><Textarea value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))} rows={3} /></div>
          </div>
          <div className="flex gap-2"><Button onClick={save} className="bg-[#002FA7] text-white text-xs rounded-lg" data-testid="save-listing-btn">Create Listing</Button><Button variant="outline" onClick={() => setShowForm(false)} className="text-xs rounded-lg">Cancel</Button></div>
        </div>
      )}

      {listings.length === 0 && !showForm ? (
        <div className="bg-white border border-slate-200 p-12 rounded-xl text-center"><Building2 size={40} className="text-slate-200 mx-auto mb-4" /><p className="text-slate-400">No listings yet. Create a listing to generate a shareable showcase page.</p></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {listings.map(l => (
            <div key={l.id} className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid={`listing-${l.id}`}>
              {/* Photo gallery */}
              {l.images && l.images.length > 0 ? (
                <div className="relative h-40 overflow-hidden bg-slate-100">
                  <img src={`${API}${l.images[0].url}`} alt={l.title} className="w-full h-full object-cover" />
                  {l.images.length > 1 && (
                    <span className="absolute bottom-2 right-2 bg-black/60 text-white text-[10px] px-2 py-0.5 rounded-full">+{l.images.length - 1} more</span>
                  )}
                </div>
              ) : (
                <div className="h-28 bg-slate-50 flex items-center justify-center">
                  <Image size={28} className="text-slate-200" />
                </div>
              )}
              <div className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div><div className="text-base font-semibold text-slate-800">{l.title}</div><div className="text-xs text-slate-400">{l.address}, {l.city}, {l.state}</div></div>
                  <Badge className={`text-[9px] ${STATUS_COLORS[l.status] || ''}`}>{l.status?.replace('_',' ')}</Badge>
                </div>
                <div className="text-2xl font-['Cabinet_Grotesk'] font-bold text-[#002FA7] mb-2">{fmt(l.price)}</div>
                <div className="flex gap-3 text-xs text-slate-500 mb-3">{l.bedrooms && <span>{l.bedrooms} beds</span>}{l.bathrooms && <span>{l.bathrooms} baths</span>}{l.sqft && <span>{l.sqft.toLocaleString()} sqft</span>}<span className="capitalize">{l.property_type}</span></div>

                {/* Photo thumbnails */}
                {l.images && l.images.length > 0 && (
                  <div className="flex gap-1.5 mb-3 flex-wrap">
                    {l.images.map((img, idx) => (
                      <div key={idx} className="relative group w-12 h-12 rounded-lg overflow-hidden border border-slate-200">
                        <img src={`${API}${img.url}`} alt="" className="w-full h-full object-cover" />
                        <button onClick={() => deletePhoto(l.id, idx)} className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                          <X size={12} className="text-white" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex gap-2 border-t border-slate-100 pt-3">
                  <input type="file" ref={fileRef} accept="image/*" onChange={e => { if (e.target.files?.[0]) uploadPhoto(l.id, e.target.files[0]); }} className="hidden" id={`photo-${l.id}`} />
                  <Button variant="outline" size="sm" className="text-[10px] h-7 gap-1" onClick={() => document.getElementById(`photo-${l.id}`)?.click()} disabled={uploading === l.id} data-testid={`upload-photo-${l.id}`}>
                    <Upload size={10} /> {uploading === l.id ? 'Uploading...' : 'Add Photo'}
                  </Button>
                  <Button variant="outline" size="sm" className="text-[10px] h-7 gap-1" onClick={() => copyLink(l.slug)}><Copy size={10} /> Link</Button>
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400" onClick={() => remove(l.id)}><Trash2 size={12} /></Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
